import { usePwaStore } from '@/store/pwa'

/**
 * How often a long-open tab re-checks for a newer service worker. Installed PWAs in
 * particular stay open across several deploys; without a poll they only discover a
 * new SW on a manual navigation. A discovered update self-activates (autoUpdate) and
 * virtual:pwa-register reloads the tab onto the fresh shell.
 */
const UPDATE_CHECK_INTERVAL_MS = 60 * 60 * 1000

/**
 * Registers the production service worker (`registerType: 'autoUpdate'` in Vite PWA).
 *
 * autoUpdate is deliberate. The generated SW self-`skipWaiting()`s + `clientsClaim()`s,
 * so a freshly-deployed SW takes control immediately rather than parking in `waiting`
 * behind a prompt. The previous 'prompt' flow surfaced an "update available" banner
 * that only ever showed in standalone display, so installed-PWA and browser users'
 * service worker never updated — the old precached shell kept being served and its
 * purged chunk hashes 404'd, i.e. icons only appeared after a *hard* refresh. Under
 * autoUpdate, virtual:pwa-register's `activated` handler reloads the page onto the new
 * shell (isUpdate/isExternal), so a plain refresh — or the poll below — self-heals a
 * stale tab. The manual update-prompt path (onNeedRefresh / setUpdateSwFn) is
 * intentionally not wired: autoUpdate never parks in `waiting`, so it would be dead.
 *
 * None of this reaches a user with no service worker, nor a stale `index.html` served
 * from an HTTP cache above it — that is what appVersionWatch.ts covers, and why
 * `usePwaStore.showUpdateNotification` no longer carries the standalone gate.
 *
 * Dynamic import keeps the virtual module out of non-prod bundles where it is stubbed.
 */
export function registerServiceWorkerDynamic() {
  void import('virtual:pwa-register').then(({ registerSW }) => {
    const pwa = usePwaStore()

    if ('serviceWorker' in navigator) {
      navigator.serviceWorker.addEventListener('controllerchange', () => {
        pwa.onServiceWorkerControllerChange()
      })
    }

    registerSW({
      immediate: true,
      onOfflineReady() {
        /* reserved for future offline UX */
      },
      onRegisteredSW(_swScriptUrl, registration) {
        pwa.onServiceWorkerRegistered()
        if (registration) {
          window.setInterval(() => {
            void registration.update()
          }, UPDATE_CHECK_INTERVAL_MS)
        }
      },
      onRegisterError(error) {
        console.warn('[PWA] service worker registration failed', error)
      },
    })
  })
}
